import type {
  ApprovalActionExecutionStatus,
  ApprovalExecutionStatus,
  ApprovalFinalizationStatus,
  ApprovalReviewStatus,
  ApprovalRoutingStatus,
  RiskLevel,
} from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type ApprovalBadgeValue =
  | ApprovalActionExecutionStatus
  | ApprovalExecutionStatus
  | ApprovalFinalizationStatus
  | ApprovalReviewStatus
  | ApprovalRoutingStatus
  | RiskLevel;

const TONES: Record<ApprovalBadgeValue, string> = {
  pending: "border-warning/20 bg-warning/10 text-warning-foreground",
  approved: "border-success/20 bg-success/10 text-success-foreground",
  rejected: "border-destructive/20 bg-destructive/10 text-destructive-foreground",
  expired: "border-destructive/20 bg-destructive/10 text-destructive-foreground",
  not_required: "border-border bg-muted text-muted-foreground",
  mixed: "border-warning/20 bg-warning/10 text-warning-foreground",
  auto: "border-success/20 bg-success/10 text-success-foreground",
  approval: "border-warning/20 bg-warning/10 text-warning-foreground",
  recommendation: "border-border bg-muted text-muted-foreground",
  processing: "border-warning/20 bg-warning/10 text-warning-foreground",
  applied: "border-success/20 bg-success/10 text-success-foreground",
  executed: "border-success/20 bg-success/10 text-success-foreground",
  queued: "border-border bg-secondary text-secondary-foreground",
  executing: "border-warning/20 bg-warning/10 text-warning-foreground",
  succeeded: "border-success/20 bg-success/10 text-success-foreground",
  partially_failed: "border-warning/20 bg-warning/10 text-warning-foreground",
  failed: "border-destructive/20 bg-destructive/10 text-destructive-foreground",
  not_applicable: "border-border bg-muted text-muted-foreground",
  low: "border-success/20 bg-success/10 text-success-foreground",
  medium: "border-warning/20 bg-warning/10 text-warning-foreground",
  high: "border-destructive/20 bg-destructive/10 text-destructive-foreground",
  critical: "border-destructive/30 bg-destructive/15 text-destructive-foreground",
};

interface ApprovalStatusBadgeProps {
  value: ApprovalBadgeValue;
  className?: string;
}

export function ApprovalStatusBadge({ value, className }: ApprovalStatusBadgeProps) {
  const { t } = useTranslation("approval");
  return (
    <Badge variant="outline" className={cn(TONES[value], className)}>
      {t(`status.${value}`)}
    </Badge>
  );
}
